import APIService from "./APIService.js";

/**
 * AlertsAPI - Frontend service for managing alerts
 * Provides methods to interact with the backend alerts endpoints
 */
class AlertsAPI {
  constructor() {
    this.endpoint = "/api/v1/app/alerts";
  }

  /**
   * Get active alerts for public display (no authentication required)
   * @returns {Promise<Array>} Array of active alert objects
   */
  async getActiveAlerts() {
    try {
      console.log("🚨 AlertsAPI: Fetching active alerts for public display");
      const alerts = await APIService.get(`${this.endpoint}/active`);
      console.log(
        "✅ AlertsAPI: Retrieved",
        alerts?.length || 0,
        "active alerts"
      );
      return alerts || [];
    } catch (error) {
      console.error("❌ AlertsAPI: Error fetching active alerts:", error);
      // Return empty array as fallback to prevent component crashes
      return [];
    }
  }

  // Note: getActiveAlertsForUser() method removed
  // because /api/v1/app/alerts/user endpoint is not implemented in AlertController.java

  // Note: getTopActiveAlerts() and getAlertStatistics() methods removed
  // because /api/v1/app/alerts/top and /api/v1/app/alerts/stats endpoints are not implemented in AlertController.java

  /**
   * Get all alerts for admin management (admin only)
   * @returns {Promise<Array>} Array of all alerts (including inactive)
   */
  async getAllAlertsForAdmin() {
    try {
      console.log("🔧 AlertsAPI: Fetching all alerts for admin management");
      const alerts = await APIService.get(`${this.endpoint}/admin`);
      console.log(
        "✅ AlertsAPI: Retrieved",
        alerts?.length || 0,
        "admin alerts"
      );
      return alerts || [];
    } catch (error) {
      console.error("❌ AlertsAPI: Error fetching admin alerts:", error);
      return [];
    }
  }

  /**
   * Create a new alert (admin only)
   * @param {Object} alertData - Alert data object
   * @returns {Promise<Object>} Created alert object
   */
  async createAlert(alertData) {
    try {
      console.log("➕ AlertsAPI: Creating new alert:", alertData.message);
      const alert = await APIService.post(this.endpoint, alertData);
      console.log("✅ AlertsAPI: Alert created successfully:", alert);
      return alert;
    } catch (error) {
      console.error("❌ AlertsAPI: Error creating alert:", error);
      throw error;
    }
  }

  /**
   * Update an existing alert (admin only)
   * @param {number} alertId - The ID of the alert to update
   * @param {Object} alertData - Updated alert data
   * @returns {Promise<Object>} Updated alert object
   */
  async updateAlert(alertId, alertData) {
    try {
      console.log("✏️ AlertsAPI: Updating alert with ID:", alertId);
      const alert = await APIService.put(
        `${this.endpoint}/${alertId}`,
        alertData
      );
      console.log("✅ AlertsAPI: Alert updated successfully:", alert);
      return alert;
    } catch (error) {
      console.error("❌ AlertsAPI: Error updating alert:", alertId, error);
      throw error;
    }
  }

  /**
   * Delete an alert (admin only) - hard delete (removes from database)
   * @param {number} alertId - The ID of the alert to delete
   * @returns {Promise<void>}
   */
  async deleteAlert(alertId) {
    try {
      console.log("🗑️ AlertsAPI: Deleting alert with ID:", alertId);
      await APIService.delete(`${this.endpoint}/${alertId}`);
      console.log("✅ AlertsAPI: Alert deleted successfully");
    } catch (error) {
      console.error("❌ AlertsAPI: Error deleting alert:", alertId, error);
      throw error;
    }
  }

  // Note: createInfoAlert() and createWarningAlert() methods removed
  // because /api/v1/app/alerts/quick/info and /api/v1/app/alerts/quick/warning endpoints are not implemented in AlertController.java
  // Use createAlert() with appropriate alertData instead

  /**
   * Deactivate an alert (admin only) - soft delete
   * @param {number} alertId - Alert ID
   * @returns {Promise<void>}
   */
  async deactivateAlert(alertId) {
    try {
      console.log(`⏹️ AlertsAPI: Deactivating alert ${alertId}`);
      await APIService.put(`${this.endpoint}/${alertId}/deactivate`);
      console.log("✅ AlertsAPI: Alert deactivated successfully");
    } catch (error) {
      console.error(
        `❌ AlertsAPI: Error deactivating alert ${alertId}:`,
        error
      );
      throw error;
    }
  }
}

// Export a singleton instance
const alertsAPI = new AlertsAPI();
export default alertsAPI;
